import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Menu,
  MenuItem,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, orderBy, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import MainLayout from '../Layout/MainLayout';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useTheme } from '../../contexts/ThemeContext';

import TerminalManagement from './TerminalManagement';
import DeviceManagement from './DeviceManagement';
import { Terminal, LegacyDevice, mapLegacyDeviceToTerminal } from '../../types/terminal';
import { Report, convertTimestamp, canUserEditReport, User, Device } from '../../types/firebase';
import { useFeatureFlag } from '../../config/featureFlags';
import { includePublicReportInSharedViews } from '../../utils/reportPrivacyDisplay';

/* card-4 style (same as Reports) */
const DetailCard = styled(Card)(({ theme }) => ({
  marginBottom: '24px',
  background: 'rgba(255, 255, 255, 0.03)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(34, 211, 238, 0.25)',
  borderTop: '1px solid rgba(34, 211, 238, 0.5)',
  borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
  borderRadius: '20px',
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    border: '1px solid rgba(34, 211, 238, 0.4)',
    boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.7), inset 0 0 20px rgba(34, 211, 238, 0.1)',
  },
}));

/* Site header — card-4 / Interactive High-Blur Glass */
const SiteHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '24px',
  padding: '24px',
  position: 'relative',
  overflow: 'hidden',
  background: 'rgba(255, 255, 255, 0.03)',
  backgroundImage: 'linear-gradient(135deg, rgba(34, 211, 238, 0.05) 0%, transparent 100%)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderTop: '1px solid rgba(34, 211, 238, 0.3)',
  borderLeft: '1px solid rgba(34, 211, 238, 0.2)',
  borderRadius: '20px',
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    border: '1px solid rgba(34, 211, 238, 0.35)',
    boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.7), inset 0 0 20px rgba(34, 211, 238, 0.08)',
  },
}));

const SiteImage = styled('div')(({ theme }) => ({
  width: '140px',
  height: '140px',
  borderRadius: '20px',
  overflow: 'hidden',
  marginRight: '30px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(10, 10, 10, 0.5)',
  border: '1px solid rgba(34, 211, 238, 0.2)',
  boxShadow: '0 0 20px rgba(0, 0, 0, 0.5)',
}));



const TabPanel: React.FC<{ children: React.ReactNode; value: number; index: number }> = ({
  children,
  value,
  index
}) => (
  <div hidden={value !== index} style={{ paddingTop: '20px' }}>
    {value === index && children}
  </div>
);



const SiteDetailsV2: React.FC = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { colors } = useTheme();
  const muiTheme = useMuiTheme();

  const [site, setSite] = useState<any>(null);
  const [terminals, setTerminals] = useState<Terminal[]>([]);
  const [legacyDevices, setLegacyDevices] = useState<LegacyDevice[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [editFormData, setEditFormData] = useState({ status: '', message: '' });


  const useTerminals = useFeatureFlag('terminals');
  const showDevicesAlias = useFeatureFlag('devicesAlias');

  const fetchSiteDetails = async () => {
    if (!siteId) {
      setError('No site ID provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      console.log('Fetching site details for siteId:', siteId);

      // Fetch site details
      const siteDoc = await getDoc(doc(db, 'sites', siteId));
      if (!siteDoc.exists()) {
        setError('Site not found');
        setLoading(false);
        return;
      }

      const siteData = { id: siteDoc.id, ...siteDoc.data() };
      console.log('Site data fetched:', siteData);
      setSite(siteData);

      // Fetch devices from existing mobile app structure
      // We'll display these with terminal UI when useTerminals flag is enabled
      try {
        const devicesCollection = collection(db, `sites/${siteId}/devices`);
        const devicesSnapshot = await getDocs(devicesCollection);
        const devicesData = devicesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as LegacyDevice[];
        console.log('Devices data fetched from mobile app structure:', devicesData);
        setLegacyDevices(devicesData);

        // If using terminals UI, convert legacy devices to terminal format for display
        if (useTerminals && devicesData.length > 0) {
          const convertedTerminals = devicesData.map(device => mapLegacyDeviceToTerminal(device));
          console.log('Converted devices to terminals for display:', convertedTerminals);
          setTerminals(convertedTerminals);
        }
      } catch (devicesErr) {
        console.warn('Failed to fetch devices:', devicesErr);
        setLegacyDevices([]);
        setTerminals([]);
      }

      // Fetch reports for this site using same permission-based approach as main Reports page
      try {
        const reportsCollection = collection(db, 'reports');
        let allReportsData: Report[] = [];

        console.log('Fetching reports for siteId:', siteId);

        if (!userProfile) {
          console.warn('No user profile available');
          setReports([]);
          return;
        }

        if (userProfile.role === 'Clerk') {
          // Clerk users only see reports from same masterOwnerID and ownerID
          const privateQuery = query(
            reportsCollection,
            where('user.masterOwnerID', '==', userProfile.masterOwnerID),
            where('user.ownerID', '==', userProfile.ownerID),
            orderBy('createdAt', 'desc')
          );
          const privateSnapshot = await getDocs(privateQuery);
          allReportsData = privateSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: convertTimestamp(doc.data().createdAt),
          })) as Report[];
        } else {
          // Admin and Tech users see public reports and private reports from their masterOwnerID

          // Fetch public reports
          const publicQuery = query(
            reportsCollection,
            where('privacy', '==', 'public'),
            orderBy('createdAt', 'desc')
          );
          const publicSnapshot = await getDocs(publicQuery);
          const publicReports = publicSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: convertTimestamp(doc.data().createdAt),
          })) as Report[];

          // Fetch private reports from same masterOwnerID
          const privateQuery = query(
            reportsCollection,
            where('privacy', '==', 'private'),
            where('user.masterOwnerID', '==', userProfile.masterOwnerID),
            orderBy('createdAt', 'desc')
          );
          const privateSnapshot = await getDocs(privateQuery);
          const privateReports = privateSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: convertTimestamp(doc.data().createdAt),
          })) as Report[];

          // Combine and sort by date
          allReportsData = [...publicReports, ...privateReports]
            .sort((a, b) => convertTimestamp(b.createdAt).getTime() - convertTimestamp(a.createdAt).getTime());
        }

        allReportsData = allReportsData.filter(includePublicReportInSharedViews);

        console.log(`Total reports fetched with permissions: ${allReportsData.length}`);
        console.log('Current siteId we are looking for:', siteId);
        console.log('Current site name:', (siteData as any).name);

        // Log first few reports to see their structure
        if (allReportsData.length > 0) {
          console.log('Sample report structures:');
          allReportsData.slice(0, 3).forEach((report, index) => {
            console.log(`Report ${index + 1}:`, {
              id: report.id,
              readerDetails_siteId: report.readerDetails?.siteId,
              readerDetails_siteName: report.readerDetails?.siteName,
              privacy: report.privacy,
              status: report.status
            });
          });
        }

        // Filter reports for this specific site
        const siteReports = allReportsData.filter(report => {
          const anyReport = report as any;

          // Log detailed report structure for debugging
          console.log(`Checking report ${report.id}:`, {
            readerDetails_siteId: anyReport.readerDetails?.siteId,
            readerDetails_siteName: anyReport.readerDetails?.siteName,
            direct_siteId: anyReport.siteId,
            location_siteId: anyReport.location?.siteId,
            site_field: anyReport.site,
            ownerId: anyReport.ownerId,
            masterOwnerId: anyReport.masterOwnerId,
            user_masterOwnerID: anyReport.user?.masterOwnerID,
            user_ownerID: anyReport.user?.ownerID
          });

          // Check the standard mobile app field first
          if (anyReport.readerDetails?.siteId === siteId) {
            console.log('✅ Matched report via readerDetails.siteId:', report.id);
            return true;
          }

          // Check if siteName matches (case insensitive)
          if (anyReport.readerDetails?.siteName &&
            (siteData as any).name &&
            anyReport.readerDetails.siteName.toLowerCase() === (siteData as any).name.toLowerCase()) {
            console.log('✅ Matched report via siteName:', report.id, anyReport.readerDetails.siteName);
            return true;
          }

          // Check other possible siteId locations
          if (anyReport.siteId === siteId) {
            console.log('✅ Matched report via direct siteId:', report.id);
            return true;
          }

          if (anyReport.location?.siteId === siteId) {
            console.log('✅ Matched report via location.siteId:', report.id);
            return true;
          }

          if (anyReport.site === siteId) {
            console.log('✅ Matched report via site field:', report.id);
            return true;
          }

          // Check if this report belongs to the same owner/site hierarchy
          const siteOwnerID = (siteData as any).ownerID;
          const siteMasterOwnerID = (siteData as any).masterOwnerID;

          console.log('Site ownership info:', { siteOwnerID, siteMasterOwnerID });

          if (siteOwnerID && anyReport.user?.ownerID === siteOwnerID &&
            anyReport.readerDetails?.siteName?.toLowerCase() === (siteData as any).name?.toLowerCase()) {
            console.log('✅ Matched report via ownerID + siteName:', report.id);
            return true;
          }

          if (siteMasterOwnerID && anyReport.user?.masterOwnerID === siteMasterOwnerID &&
            anyReport.readerDetails?.siteName?.toLowerCase() === (siteData as any).name?.toLowerCase()) {
            console.log('✅ Matched report via masterOwnerID + siteName:', report.id);
            return true;
          }

          // As a broad fallback, if exact site matching fails, show reports from the same masterOwnerID
          // This is a temporary solution until we understand the exact data structure
          if (siteMasterOwnerID && anyReport.user?.masterOwnerID === siteMasterOwnerID) {
            console.log('🔶 Matched report via masterOwnerID fallback (same organization):', report.id);
            return true;
          }

          return false;
        });

        console.log(`Reports filtered for site ${siteId}: ${siteReports.length} found`);
        setReports(siteReports);
      } catch (reportsErr) {
        console.warn('Failed to fetch reports:', reportsErr);
        setReports([]);
      }

    } catch (err: any) {
      console.error('Error fetching site details:', err);
      setError(`Failed to fetch site details: ${err.message || err.toString()}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSiteDetails();
  }, [siteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBack = () => {
    navigate(-1);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };



  // Device count from the mobile app structure
  const totalDeviceCount = legacyDevices.length;

  // Handle updates from terminal management
  const handleTerminalsUpdate = (updatedTerminals: Terminal[]) => {
    setTerminals(updatedTerminals);
  };

  // Handle updates from legacy device management (Device[] from DeviceManagement; normalize to LegacyDevice[])
  const handleDevicesUpdate = (updatedDevices: Device[]) => {
    setLegacyDevices(updatedDevices.map((d) => ({ ...d, id: d.id ?? '' } as LegacyDevice)));
  };

  // Handle action menu
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, report: Report) => {
    setAnchorEl(event.currentTarget);
    setSelectedReport(report);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedReport(null);
  };

  // Handle edit report
  const handleEditClick = () => {
    if (selectedReport) {
      setEditFormData({
        status: selectedReport.status,
        message: (selectedReport as any).message || ''
      });
      setEditDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleEditSave = async () => {
    if (!selectedReport) return;

    try {
      const reportRef = doc(db, 'reports', selectedReport.id);
      await updateDoc(reportRef, {
        status: editFormData.status,
        message: editFormData.message,
        updatedAt: new Date()
      });

      // Update local state
      setReports(prevReports =>
        prevReports.map(report =>
          report.id === selectedReport.id
            ? { ...report, status: editFormData.status as Report['status'], message: editFormData.message }
            : report
        )
      );

      setSnackbarMessage('Report updated successfully');
      setSnackbarOpen(true);
      setEditDialogOpen(false);
    } catch (error) {
      console.error('Error updating report:', error);
      setSnackbarMessage('Failed to update report');
      setSnackbarOpen(true);
    }
  };

  // Handle delete report
  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedReport) return;

    try {
      await deleteDoc(doc(db, 'reports', selectedReport.id));

      // Update local state
      setReports(prevReports => prevReports.filter(report => report.id !== selectedReport.id));

      setSnackbarMessage('Report deleted successfully');
      setSnackbarOpen(true);
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error deleting report:', error);
      setSnackbarMessage('Failed to delete report');
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  if (loading) {
    return (
      <MainLayout title="Loading Site...">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  if (error || !site) {
    return (
      <MainLayout title="Error">
        <Alert severity="error">{error || 'Site not found'}</Alert>
        <Button onClick={handleBack} sx={{ marginTop: 2 }}>
          Go Back
        </Button>
      </MainLayout>
    );
  }

  return (
    <MainLayout title={site.name} showDock={false}>
      <Box>
        {/* Header with Back Button */}
        <Box display="flex" alignItems="center" marginBottom={3}>
          <IconButton
            onClick={handleBack}
            sx={{
              color: '#ffffff',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '50px',
              marginRight: 2,
              '&:hover': { background: 'rgba(255, 255, 255, 0.2)' },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            component="span"
            sx={{
              fontFamily: "'Inter', sans-serif",
              color: '#ffffff',
              fontSize: '15px',
              fontWeight: 700,
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
            }}
          >
            Site Details
          </Typography>
        </Box>

        {/* Site Header Card */}
        <SiteHeader>
          <SiteImage>
            {site.photo ? (
              <img
                src={site.photo}
                alt={site.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <Avatar
                sx={{
                  width: 100,
                  height: 100,
                  backgroundColor: 'rgba(34, 211, 238, 0.1)',
                  color: '#22d3ee',
                  border: '2px solid rgba(34, 211, 238, 0.5)',
                  boxShadow: '0 0 20px rgba(34, 211, 238, 0.3)',
                  fontSize: '36px',
                  fontWeight: 'bold',
                }}
              >
                {site.name.charAt(0)}
              </Avatar>
            )}
          </SiteImage>

          <Box flex={1}>
            <Typography variant="h4" color={muiTheme.palette.text.primary} fontWeight="bold" gutterBottom>
              {site.name}
            </Typography>

            <Box marginBottom={1}>
              <Typography color={muiTheme.palette.text.secondary}>
                {site.address}
              </Typography>
            </Box>

            {site.contactPerson && (
              <Box marginBottom={1}>
                <Typography color={muiTheme.palette.text.secondary}>
                  {site.contactPerson}
                  {site.contactPhone && ` • ${site.contactPhone}`}
                </Typography>
              </Box>
            )}

            <Box display="flex" alignItems="center" gap={1} marginTop={2}>
              <Chip
                label={`${totalDeviceCount} ${useTerminals ? 'terminals' : 'devices'}`}
                size="small"
                sx={{
                  color: '#ffffff',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(16px)',
                  borderRadius: '20px',
                  fontWeight: 'bold',
                }}
              />
              <Chip
                label={`${reports.length} reports`}
                size="small"
                sx={{
                  color: '#ffffff',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(16px)',
                  borderRadius: '20px',
                  fontWeight: 'bold',
                }}
              />
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box display="flex" flexDirection="column" gap={1}>
            {/* Edit Site button removed */}
          </Box>
        </SiteHeader>

        {/* Site Description */}
        {site.description && (
          <DetailCard>
            <CardContent>
              <Typography
                component="span"
                sx={{
                  fontFamily: "'JetBrains Mono', monospace",
                  color: '#22d3ee',
                  fontSize: '13px',
                  fontWeight: 500,
                  letterSpacing: '0.3em',
                  textTransform: 'uppercase',
                  display: 'block',
                  marginBottom: 1.5,
                }}
              >
                Description
              </Typography>
              <Typography color={muiTheme.palette.text.secondary}>
                {site.description}
              </Typography>
            </CardContent>
          </DetailCard>
        )}

        {/* Tabs — pill style (same as Reports Overview toggles) */}
        <DetailCard>
          <CardContent sx={{ py: 2, px: 3, '&:last-child': { pb: 2 } }}>
            <Box sx={{ borderBottom: 1, borderColor: 'rgba(34, 211, 238, 0.2)', paddingBottom: 2, marginBottom: 2 }}>
              <Box display="flex" gap={0.5} flexWrap="wrap" sx={{ '& .MuiButton-root': { minWidth: 0 } }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setCurrentTab(0)}
                  sx={{
                    backgroundColor: currentTab === 0 ? 'rgba(34, 211, 238, 0.15)' : 'transparent',
                    borderColor: currentTab === 0 ? 'rgba(34, 211, 238, 0.5)' : 'rgba(255, 255, 255, 0.1)',
                    color: currentTab === 0 ? muiTheme.palette.text.primary : muiTheme.palette.text.secondary,
                    boxShadow: currentTab === 0 ? 'inset 0 0 20px rgba(34, 211, 238, 0.1)' : 'none',
                    borderRadius: '50px',
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 'bold',
                    textTransform: 'none',
                    fontSize: '0.8125rem',
                    py: 0.5,
                    px: 1.5,
                    '&:hover': {
                      backgroundColor: currentTab === 0 ? 'rgba(34, 211, 238, 0.25)' : 'rgba(255, 255, 255, 0.05)',
                      borderColor: currentTab === 0 ? '#22d3ee' : 'rgba(255, 255, 255, 0.2)',
                      color: muiTheme.palette.text.primary,
                    },
                  }}
                >
                  {useTerminals ? 'Terminals' : 'Devices'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setCurrentTab(1)}
                  sx={{
                    backgroundColor: currentTab === 1 ? 'rgba(34, 211, 238, 0.15)' : 'transparent',
                    borderColor: currentTab === 1 ? 'rgba(34, 211, 238, 0.5)' : 'rgba(255, 255, 255, 0.1)',
                    color: currentTab === 1 ? muiTheme.palette.text.primary : muiTheme.palette.text.secondary,
                    boxShadow: currentTab === 1 ? 'inset 0 0 20px rgba(34, 211, 238, 0.1)' : 'none',
                    borderRadius: '50px',
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 'bold',
                    textTransform: 'none',
                    fontSize: '0.8125rem',
                    py: 0.5,
                    px: 1.5,
                    '&:hover': {
                      backgroundColor: currentTab === 1 ? 'rgba(34, 211, 238, 0.25)' : 'rgba(255, 255, 255, 0.05)',
                      borderColor: currentTab === 1 ? '#22d3ee' : 'rgba(255, 255, 255, 0.2)',
                      color: muiTheme.palette.text.primary,
                    },
                  }}
                >
                  Recent Reports
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setCurrentTab(2)}
                  sx={{
                    backgroundColor: currentTab === 2 ? 'rgba(34, 211, 238, 0.15)' : 'transparent',
                    borderColor: currentTab === 2 ? 'rgba(34, 211, 238, 0.5)' : 'rgba(255, 255, 255, 0.1)',
                    color: currentTab === 2 ? muiTheme.palette.text.primary : muiTheme.palette.text.secondary,
                    boxShadow: currentTab === 2 ? 'inset 0 0 20px rgba(34, 211, 238, 0.1)' : 'none',
                    borderRadius: '50px',
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 'bold',
                    textTransform: 'none',
                    fontSize: '0.8125rem',
                    py: 0.5,
                    px: 1.5,
                    '&:hover': {
                      backgroundColor: currentTab === 2 ? 'rgba(34, 211, 238, 0.25)' : 'rgba(255, 255, 255, 0.05)',
                      borderColor: currentTab === 2 ? '#22d3ee' : 'rgba(255, 255, 255, 0.2)',
                      color: muiTheme.palette.text.primary,
                    },
                  }}
                >
                  Analytics
                </Button>
              </Box>
            </Box>
          </CardContent>

          <TabPanel value={currentTab} index={0}>
            <CardContent>
              {useTerminals ? (
                <TerminalManagement
                  siteId={siteId!}
                  siteName={site.name}
                  terminals={terminals}
                  onTerminalsUpdate={handleTerminalsUpdate}
                />
              ) : (
                <DeviceManagement
                  siteId={siteId!}
                  siteName={site.name}
                  devices={legacyDevices as any}
                  onDevicesUpdate={handleDevicesUpdate}
                />
              )}

              {/* Debug information */}
              {process.env.NODE_ENV === 'development' && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: muiTheme.palette.background.default, borderRadius: 1 }}>
                  <Typography variant="caption" color={muiTheme.palette.text.secondary}>
                    Debug: useTerminals={useTerminals.toString()}, showDevicesAlias={showDevicesAlias.toString()}<br />
                    Terminals: {terminals.length}, Legacy Devices: {legacyDevices.length}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </TabPanel>

          <TabPanel value={currentTab} index={1}>
            <CardContent>
              <Typography
                component="span"
                sx={{
                  fontFamily: "'JetBrains Mono', monospace",
                  color: '#22d3ee',
                  fontSize: '13px',
                  fontWeight: 500,
                  letterSpacing: '0.3em',
                  textTransform: 'uppercase',
                  display: 'block',
                  marginBottom: 2,
                }}
              >
                Recent Scan Reports ({reports.length})
              </Typography>

              {reports.length === 0 ? (
                <Box textAlign="center" padding={4}>
                  <Typography color={muiTheme.palette.text.secondary}>
                    No reports available for this site
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {reports.slice(0, 10).map((report) => (
                    <Card
                      key={report.id}
                      sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        marginBottom: 2,
                        background: 'rgba(255, 255, 255, 0.03)',
                        backgroundImage: 'linear-gradient(135deg, rgba(34, 211, 238, 0.05) 0%, transparent 100%)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        borderTop: '1px solid rgba(255, 255, 255, 0.15)',
                        borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '20px',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05)',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.06)',
                          border: '1px solid rgba(34, 211, 238, 0.3)',
                          transform: 'translateY(-5px)',
                          boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.7), inset 0 0 20px rgba(34, 211, 238, 0.1)',
                        },
                      }}
                    >
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" marginBottom={2}>
                          <Box flex={1}>
                            <Box display="flex" alignItems="center" gap={1} marginBottom={1}>
                              <Typography
                                variant="h6"
                                color={muiTheme.palette.text.primary}
                                sx={{ fontWeight: 'bold' }}
                              >
                                {(report as any).readerDetails?.siteName || site.name}
                              </Typography>
                              <Chip
                                label={report.privacy}
                                size="small"
                                sx={{
                                  color: '#ffffff',
                                  border: '1px solid rgba(255, 255, 255, 0.15)',
                                  background: 'rgba(255, 255, 255, 0.15)',
                                  backdropFilter: 'blur(16px)',
                                  borderRadius: '20px',
                                  fontWeight: 'bold',
                                }}
                              />
                            </Box>

                            <Typography variant="body2" color={muiTheme.palette.text.secondary} gutterBottom>
                              {(report as any).location?.address || site.address}
                            </Typography>

                            <Typography variant="body2" color={muiTheme.palette.text.disabled}>
                              {convertTimestamp(report.createdAt).toLocaleDateString()} at {convertTimestamp(report.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </Box>

                          <Chip
                            label={report.status}
                            size="small"
                            sx={{
                              backgroundColor: report.status === 'No Skimmer Detected' ? colors.Success :
                                report.status === 'Skimmer Detected!' ? colors.Error : colors.Warning,
                              color: muiTheme.palette.primary.contrastText,
                              fontWeight: 'bold',
                            }}
                          />
                        </Box>

                        {(report as any).readerDetails?.device && (
                          <Box marginBottom={2}>
                            <Typography variant="subtitle2" color={muiTheme.palette.text.primary} gutterBottom>
                              Device Information
                            </Typography>
                            <Typography variant="body2" color={muiTheme.palette.text.secondary}>
                              <strong>Type:</strong> {(report as any).readerDetails.device.deviceType || 'N/A'}
                            </Typography>
                            {(report as any).readerDetails.device.modelName && (
                              <Typography variant="body2" color={muiTheme.palette.text.secondary}>
                                <strong>Model:</strong> {(report as any).readerDetails.device.modelName}
                                {(report as any).readerDetails.device.modelNumber && `(${(report as any).readerDetails.device.modelNumber})`}
                              </Typography>
                            )}
                            {(report as any).readerDetails.device.specificLocation && (
                              <Typography variant="body2" color={muiTheme.palette.text.secondary}>
                                <strong>Location:</strong> {(report as any).readerDetails.device.specificLocation}
                              </Typography>
                            )}
                          </Box>
                        )}

                        <Box marginBottom={2}>
                          <Typography variant="subtitle2" color={muiTheme.palette.text.primary} gutterBottom>
                            Scan Details
                          </Typography>
                          <Typography variant="body2" color={muiTheme.palette.text.secondary}>
                            <strong>Scanned by:</strong> {report.user?.name || 'Unknown'} ({report.user?.role || 'N/A'})
                          </Typography>
                          <Typography variant="body2" color={muiTheme.palette.text.secondary}>
                            <strong>Result:</strong> {report.status}
                          </Typography>
                          {(report as any).message && (
                            <Typography variant="body2" color={muiTheme.palette.text.secondary}>
                              <strong>Notes:</strong> {(report as any).message}
                            </Typography>
                          )}
                        </Box>

                        {(report as any).location?.assignedUsers && (report as any).location.assignedUsers.length > 0 && (
                          <Box marginBottom={2}>
                            <Typography variant="subtitle2" color={muiTheme.palette.text.primary} gutterBottom>
                              Assigned Users
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                              {(report as any).location.assignedUsers.map((user: any, index: number) => (
                                <Chip
                                  key={index}
                                  label={`${user.name} (${user.role})`}
                                  size="small"
                                  sx={{
                                    backgroundColor: muiTheme.palette.info.main,
                                    color: muiTheme.palette.info.contrastText,
                                    fontSize: '0.7rem',
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}

                        {/* Action buttons for Admin and Tech users — pill style (same as Reports) */}
                        <Box display="flex" justifyContent="space-between" alignItems="center" marginTop={2} flexWrap="wrap" gap={1}>
                          <Button
                            variant="outlined"
                            size="small"
                            disabled
                            sx={{
                              color: muiTheme.palette.text.disabled,
                              borderColor: 'rgba(255, 255, 255, 0.1)',
                              borderRadius: '50px',
                              fontWeight: 'bold',
                            }}
                          >
                            View Details
                          </Button>

                          {userProfile && canUserEditReport(report, userProfile as User) && (
                            <Box display="flex" gap={1}>
                              <Button
                                startIcon={<EditIcon />}
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  setSelectedReport(report);
                                  setEditFormData({
                                    status: report.status,
                                    message: (report as any).message || ''
                                  });
                                  setEditDialogOpen(true);
                                }}
                                sx={{
                                  color: '#ffffff',
                                  border: '1px solid rgba(255, 255, 255, 0.15)',
                                  background: 'rgba(255, 255, 255, 0.15)',
                                  backdropFilter: 'blur(16px)',
                                  borderRadius: '50px',
                                  fontWeight: 'bold',
                                  '&:hover': {
                                    transform: 'translateY(-2px)',
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    borderColor: 'rgba(255, 255, 255, 0.25)',
                                  },
                                }}
                              >
                                Edit
                              </Button>
                              <Button
                                startIcon={<DeleteIcon />}
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  setSelectedReport(report);
                                  setDeleteDialogOpen(true);
                                }}
                                sx={{
                                  color: '#ffffff',
                                  border: '1px solid rgba(255, 255, 255, 0.15)',
                                  background: 'rgba(255, 255, 255, 0.15)',
                                  backdropFilter: 'blur(16px)',
                                  borderRadius: '50px',
                                  fontWeight: 'bold',
                                  '&:hover': {
                                    transform: 'translateY(-2px)',
                                    background: 'rgba(244, 67, 54, 0.3)',
                                    borderColor: 'rgba(244, 67, 54, 0.5)',
                                  },
                                }}
                              >
                                Delete
                              </Button>
                              <IconButton
                                onClick={(e) => handleMenuClick(e, report)}
                                size="small"
                                sx={{
                                  color: '#ffffff',
                                  border: '1px solid rgba(255, 255, 255, 0.15)',
                                  background: 'rgba(255, 255, 255, 0.1)',
                                  borderRadius: '50px',
                                  '&:hover': { background: 'rgba(255, 255, 255, 0.2)' },
                                }}
                              >
                                <MoreVertIcon />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </CardContent>
          </TabPanel>

          <TabPanel value={currentTab} index={2}>
            <CardContent>
              <Typography
                component="span"
                sx={{
                  fontFamily: "'JetBrains Mono', monospace",
                  color: '#22d3ee',
                  fontSize: '13px',
                  fontWeight: 500,
                  letterSpacing: '0.3em',
                  textTransform: 'uppercase',
                  display: 'block',
                  marginBottom: 2,
                }}
              >
                Site Analytics
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 3 }}>
                <Box
                  sx={{
                    textAlign: 'center',
                    padding: 2,
                    position: 'relative',
                    overflow: 'hidden',
                    background: 'rgba(255, 255, 255, 0.03)',
                    backgroundImage: 'linear-gradient(135deg, rgba(34, 211, 238, 0.05) 0%, transparent 100%)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderTop: '1px solid rgba(255, 255, 255, 0.15)',
                    borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '20px',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.06)',
                      border: '1px solid rgba(34, 211, 238, 0.3)',
                      transform: 'translateY(-5px)',
                      boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.7), inset 0 0 20px rgba(34, 211, 238, 0.1)',
                    },
                  }}
                >
                  <Typography variant="h4" color={muiTheme.palette.primary.main} fontWeight="bold">
                    {totalDeviceCount}
                  </Typography>
                  <Typography color={muiTheme.palette.text.secondary}>
                    Active {useTerminals ? 'Terminals' : 'Devices'}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    textAlign: 'center',
                    padding: 2,
                    position: 'relative',
                    overflow: 'hidden',
                    background: 'rgba(255, 255, 255, 0.03)',
                    backgroundImage: 'linear-gradient(135deg, rgba(34, 211, 238, 0.05) 0%, transparent 100%)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderTop: '1px solid rgba(255, 255, 255, 0.15)',
                    borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '20px',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.06)',
                      border: '1px solid rgba(34, 211, 238, 0.3)',
                      transform: 'translateY(-5px)',
                      boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.7), inset 0 0 20px rgba(34, 211, 238, 0.1)',
                    },
                  }}
                >
                  <Typography variant="h4" color={muiTheme.palette.success.main} fontWeight="bold">
                    {reports.filter(r => r.status === 'No Skimmer Detected').length}
                  </Typography>
                  <Typography color={muiTheme.palette.text.secondary}>
                    Safe Scans
                  </Typography>
                </Box>

                <Box
                  sx={{
                    textAlign: 'center',
                    padding: 2,
                    position: 'relative',
                    overflow: 'hidden',
                    background: 'rgba(255, 255, 255, 0.03)',
                    backgroundImage: 'linear-gradient(135deg, rgba(34, 211, 238, 0.05) 0%, transparent 100%)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderTop: '1px solid rgba(255, 255, 255, 0.15)',
                    borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '20px',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.06)',
                      border: '1px solid rgba(34, 211, 238, 0.3)',
                      transform: 'translateY(-5px)',
                      boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.7), inset 0 0 20px rgba(34, 211, 238, 0.1)',
                    },
                  }}
                >
                  <Typography variant="h4" color={muiTheme.palette.error.main} fontWeight="bold">
                    {reports.filter(r => r.status === 'Skimmer Detected!').length}
                  </Typography>
                  <Typography color={muiTheme.palette.text.secondary}>
                    Risks Found
                  </Typography>
                </Box>

                <Box
                  sx={{
                    textAlign: 'center',
                    padding: 2,
                    position: 'relative',
                    overflow: 'hidden',
                    background: 'rgba(255, 255, 255, 0.03)',
                    backgroundImage: 'linear-gradient(135deg, rgba(34, 211, 238, 0.05) 0%, transparent 100%)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderTop: '1px solid rgba(255, 255, 255, 0.15)',
                    borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '20px',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.06)',
                      border: '1px solid rgba(34, 211, 238, 0.3)',
                      transform: 'translateY(-5px)',
                      boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.7), inset 0 0 20px rgba(34, 211, 238, 0.1)',
                    },
                  }}
                >
                  <Typography variant="h4" color={muiTheme.palette.info.main} fontWeight="bold">
                    {reports.length}
                  </Typography>
                  <Typography color={muiTheme.palette.text.secondary}>
                    Total Scans
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </TabPanel>
        </DetailCard>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={handleEditClick}>
            <EditIcon sx={{ marginRight: 1 }} />
            Edit Report
          </MenuItem>
          <MenuItem onClick={handleDeleteClick} sx={{ color: muiTheme.palette.error.main }}>
            <DeleteIcon sx={{ marginRight: 1 }} />
            Delete Report
          </MenuItem>
        </Menu>

        {/* Edit Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              background: 'rgba(255, 255, 255, 0.03)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(34, 211, 238, 0.25)',
              borderTop: '1px solid rgba(34, 211, 238, 0.5)',
              borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
              borderRadius: '20px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
            },
          }}
        >
          <DialogTitle
            sx={{
              fontFamily: "'Inter', sans-serif",
              color: '#ffffff',
              fontSize: '15px',
              fontWeight: 700,
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
              padding: '20px 24px',
              borderBottom: '1px solid rgba(34, 211, 238, 0.2)',
              backgroundColor: 'transparent',
            }}
          >
            Edit Report
          </DialogTitle>
          <DialogContent sx={{ backgroundColor: 'transparent', padding: '24px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 1 }}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Status
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {['No Skimmer Detected', 'Skimmer Detected!', 'Inconclusive'].map((status) => (
                    <Chip
                      key={status}
                      label={status}
                      clickable
                      variant={editFormData.status === status ? 'filled' : 'outlined'}
                      onClick={() => setEditFormData({ ...editFormData, status })}
                      sx={{
                        backgroundColor: editFormData.status === status ?
                          (status === 'No Skimmer Detected' ? colors.Success :
                            status === 'Skimmer Detected!' ? colors.Error : colors.Warning) : 'transparent',
                        color: editFormData.status === status ? muiTheme.palette.primary.contrastText : muiTheme.palette.text.primary,
                        borderColor: status === 'No Skimmer Detected' ? colors.Success :
                          status === 'Skimmer Detected!' ? colors.Error : colors.Warning,
                      }}
                    />
                  ))}
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Message/Notes
                </Typography>
                <textarea
                  value={editFormData.message}
                  onChange={(e) => setEditFormData({ ...editFormData, message: e.target.value })}
                  placeholder="Add notes or comments about this report..."
                  style={{
                    width: '100%',
                    minHeight: '100px',
                    padding: '12px',
                    border: `1px solid ${muiTheme.palette.divider}`,
                    borderRadius: '4px',
                    backgroundColor: muiTheme.palette.background.paper,
                    color: muiTheme.palette.text.primary,
                    fontFamily: muiTheme.typography.fontFamily,
                    fontSize: '14px',
                    resize: 'vertical',
                  }}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ padding: '16px 24px', gap: 1, borderTop: '1px solid rgba(34, 211, 238, 0.2)', backgroundColor: 'transparent' }}>
            <Button
              onClick={() => setEditDialogOpen(false)}
              variant="outlined"
              sx={{
                color: '#ffffff',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '50px',
                fontWeight: 'bold',
                px: 2,
                '&:hover': { background: 'rgba(255, 255, 255, 0.2)', borderColor: 'rgba(255, 255, 255, 0.25)' },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditSave}
              variant="outlined"
              sx={{
                color: '#ffffff',
                border: '1px solid rgba(34, 211, 238, 0.5)',
                background: 'rgba(34, 211, 238, 0.15)',
                borderRadius: '50px',
                fontWeight: 'bold',
                px: 2,
                '&:hover': { background: 'rgba(34, 211, 238, 0.25)', borderColor: '#22d3ee' },
              }}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          PaperProps={{
            sx: {
              background: 'rgba(255, 255, 255, 0.03)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(34, 211, 238, 0.25)',
              borderTop: '1px solid rgba(34, 211, 238, 0.5)',
              borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
              borderRadius: '20px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
            },
          }}
        >
          <DialogTitle
            sx={{
              fontFamily: "'Inter', sans-serif",
              color: '#ffffff',
              fontSize: '15px',
              fontWeight: 700,
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
              padding: '20px 24px',
              borderBottom: '1px solid rgba(34, 211, 238, 0.2)',
              backgroundColor: 'transparent',
            }}
          >
            Delete Report
          </DialogTitle>
          <DialogContent sx={{ backgroundColor: 'transparent', padding: '24px' }}>
            <Typography color="text.primary">
              Are you sure you want to delete this report? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ padding: '16px 24px', gap: 1, borderTop: '1px solid rgba(34, 211, 238, 0.2)', backgroundColor: 'transparent' }}>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              variant="outlined"
              sx={{
                color: '#ffffff',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '50px',
                fontWeight: 'bold',
                px: 2,
                '&:hover': { background: 'rgba(255, 255, 255, 0.2)', borderColor: 'rgba(255, 255, 255, 0.25)' },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="outlined"
              sx={{
                color: '#ffffff',
                border: '1px solid rgba(244, 67, 54, 0.5)',
                background: 'rgba(244, 67, 54, 0.15)',
                borderRadius: '50px',
                fontWeight: 'bold',
                px: 2,
                '&:hover': { background: 'rgba(244, 67, 54, 0.3)', borderColor: 'rgba(244, 67, 54, 0.7)' },
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          message={snackbarMessage}
        />

      </Box>
    </MainLayout>
  );
};

export default SiteDetailsV2;
